import { generateAllRateScadenze, type RataScadenza } from './financeCalculations';
import type { Finanziamento, Scadenza } from '../types';

/**
 * Voce della timeline unificata: una rata calcolata da un finanziamento oppure
 * una scadenza registrata dall'utente. Il campo `kind` distingue le due nature,
 * così la UI può renderizzarle diversamente senza type guard sparsi.
 */
export type VoceScadenza =
  | {
      kind: 'rata';
      id: string;
      date: string; // ISO yyyy-mm-dd
      titolo: string;
      importo: number;
      rata: RataScadenza;
    }
  | {
      kind: 'promemoria';
      id: string;
      date: string;
      titolo: string;
      completata: boolean;
      categoria: string | null;
      note: string | null;
      scadenza: Scadenza;
    };

export interface TimelineScadenze {
  /** Rate future + promemoria non completati, ordinati per data crescente. */
  voci: VoceScadenza[];
  /** La voce più imminente, rata o promemoria che sia. */
  prossima: VoceScadenza | null;
  /** Promemoria con data passata e non ancora completati. */
  scadute: VoceScadenza[];
  /** Voci entro `giorniImminenti` da oggi. */
  imminenti: VoceScadenza[];
}

/** Normalizza una data (stringa ISO o Date) a mezzanotte UTC, in millisecondi. */
function toDayTs(value: string | Date): number {
  const s = typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
  return new Date(`${s}T00:00:00.000Z`).getTime();
}

export function buildTimelineScadenze(
  finanziamenti: Finanziamento[],
  scadenze: Scadenza[],
  oggi: Date = new Date(),
  giorniImminenti = 30
): TimelineScadenze {
  const oggiTs = toDayTs(oggi);

  const rate: VoceScadenza[] = generateAllRateScadenze(finanziamenti, oggi).map((r) => ({
    kind: 'rata',
    id: `${r.finanziamentoId}-${r.numero}`,
    date: r.date,
    titolo: r.finanziamentoNome,
    importo: r.importo,
    rata: r,
  }));

  const promemoria: VoceScadenza[] = scadenze.map((s) => ({
    kind: 'promemoria',
    id: s.id,
    date: s.data.slice(0, 10),
    titolo: s.titolo,
    completata: s.completata,
    categoria: s.categoria,
    note: s.note,
    scadenza: s,
  }));

  // Scadute: solo promemoria passati e non completati. Le rate passate sono già
  // escluse da generateAllRateScadenze, che filtra a partire da oggi.
  const scadute = promemoria
    .filter((v) => v.kind === 'promemoria' && !v.completata && toDayTs(v.date) < oggiTs)
    .sort((a, b) => a.date.localeCompare(b.date));

  const futuri = promemoria.filter(
    (v) => v.kind === 'promemoria' && !v.completata && toDayTs(v.date) >= oggiTs
  );

  const voci = [...rate, ...futuri].sort((a, b) => a.date.localeCompare(b.date));

  const limiteTs = oggiTs + giorniImminenti * 24 * 60 * 60 * 1000;
  const imminenti = voci.filter((v) => toDayTs(v.date) <= limiteTs);

  return {
    voci,
    prossima: voci[0] ?? null,
    scadute,
    imminenti,
  };
}
